import React, { useState, useEffect, useRef, useCallback } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { flowsList, flowsSync, deleteFlow, DeprecateFlow } from '../../../api/Api';
import { useSelector } from 'react-redux';
import FlowListHeader from './components/FlowListHeader';
import FlowList from './components/FlowList';
import { useFeature } from '../../../../../hooks/useFeatureGating';
import { FEATURES } from '../../../../../constant/featureSlugs';
import PremiumUpgradeScreen from '../../../../../components/commonComponents/PremiumUpgradeScreen/PremiumUpgradeScreen';

const Flows = () => {
  const { currentBrand } = useSelector((state) => state.app);
  const searchInputRef = useRef(null);
  const navigate = useNavigate();

  const { isEnabled: isFlowsEnabled } = useFeature(FEATURES.FLOWS);
  
  // State
  const [searchText, setSearchText] = useState('');
  const [selectedStatus, setSelectedStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingNextPage, setIsLoadingNextPage] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [flows, setFlows] = useState([]);
  const [hasMore, setHasMore] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0,
  });
  const [actionInProgressId, setActionInProgressId] = useState(null);

  const resolveFlowIdentifier = (flow) => flow?.id ?? flow?.flow_id;
  const toIdentifierKey = (identifier) => {
    if (identifier === null || identifier === undefined) return undefined;
    return String(identifier);
  };

  // Fetch flows data
  const fetchFlows = useCallback(async (isLoadMore = false) => {
    if (!currentBrand?.brand_id) return 0;
    
    try {
      if (isLoadMore) {
        setIsLoadingNextPage(true);
      } else {
        setIsLoading(true);
      }
      
      const query = {
        page: isLoadMore ? pagination.page + 1 : 1,
        limit: pagination.limit,
        search: searchText || undefined,
        status: selectedStatus.length > 0 ? selectedStatus.join(',') : undefined
      };
      
      const response = await flowsList(currentBrand.brand_id, query);
      const newFlows = response?.data?.flows || [];

      const totalFromResponse =
        response?.data?.pagination?.total ??
        response?.data?.total ??
        response?.data?.count ??
        response?.data?.flow_count ??
        response?.count ??
        (isLoadMore ? pagination.total : newFlows.length);

      const deriveHasMore = () => {
        if (typeof response?.data?.pagination?.has_more === 'boolean') {
          return response.data.pagination.has_more;
        }
        if (typeof response?.data?.has_more === 'boolean') {
          return response.data.has_more;
        }
        if (typeof response?.data?.pagination?.next_page !== 'undefined') {
          return Boolean(response.data.pagination.next_page);
        }
        if (
          typeof response?.data?.pagination?.current_page !== 'undefined' &&
          typeof response?.data?.pagination?.total_pages !== 'undefined'
        ) {
          return (
            response.data.pagination.current_page <
            response.data.pagination.total_pages
          );
        }
        return newFlows.length === query.limit;
      };

      const nextHasMore = isLoadMore
        ? newFlows.length > 0 && deriveHasMore()
        : deriveHasMore();
      
      setFlows(prev => isLoadMore ? [...prev, ...newFlows] : newFlows);
      setPagination(prev => ({
        ...prev,
        page: query.page,
        total: totalFromResponse,
      }));
      setHasMore(nextHasMore);
      
      return newFlows.length;
    } catch (error) {
      console.error('Error fetching flows:', error);
      message.error('Failed to fetch flows. Please try again.');
      return 0;
    } finally {
      setIsLoading(false);
      setIsLoadingNextPage(false);
    }
  }, [currentBrand?.brand_id, pagination.page, pagination.limit, pagination.total, searchText, selectedStatus]);

  // Initial fetch
  useEffect(() => {
    fetchFlows();
  }, [fetchFlows]);

  // Handle search
  const handleSearchChange = (value) => {
    setSearchText(value);
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle filter change
  const handleFilterChange = (filters, data) => {
    if (!data?.status) return;

    setSelectedStatus(prev => {
      const alreadySelected = prev.includes(data.status);
      const next = alreadySelected
        ? prev.filter(status => status !== data.status)
        : [...prev, data.status];
      return next;
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle clear all filters
  const handleClearFilters = () => {
    setSelectedStatus([]);
    setSearchText('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle sync action
  const handleSync = async () => {
    if (!currentBrand?.brand_id) return;
    
    setIsSyncing(true);
    try {
      await flowsSync(currentBrand.brand_id);
      message.success('Flows synced successfully');
      fetchFlows(); // Refresh the list after sync
    } catch (error) {
      console.error('Error syncing flows:', error);
      message.error('Failed to sync flows. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  // Handle infinite scroll
  const handleLoadMore = async () => {
    if (isLoading || isLoadingNextPage || !hasMore) return;
    
    const newItems = await fetchFlows(true);
  };

  // Handle flow selection
  const handleFlowSelect = (flow) => {
    const flowIdentifier = flow?.id ?? flow?.flow_id;

    if (!flowIdentifier) {
      message.warning('Unable to open flow. Missing flow ID.');
      return;
    }

    const status = typeof flow?.status === 'string' ? flow.status.toUpperCase() : '';

    if (status === 'PUBLISHED' || status === 'DEPRECATED') {
      // message.info('This flow is read-only. Showing insights instead of the editor.');
      navigate(`/user/channels/whatsapp/flows/${flowIdentifier}/insights`);
      return;
    }

    navigate(`/user/channels/whatsapp/flows/${flowIdentifier}/edit`);
  };

  const handleDeprecateFlowAction = async (flow) => {
    if (!flow) return;
    if (flow.status !== 'PUBLISHED') {
      message.warning('Only published flows can be deprecated.');
      return;
    }

    const flowIdentifier = resolveFlowIdentifier(flow);
    const identifierKey = toIdentifierKey(flowIdentifier);

    if (!flowIdentifier || !identifierKey) {
      message.error('Unable to deprecate flow. Missing flow ID.');
      return;
    }

    if (!currentBrand?.brand_id) {
      message.error('Please select a brand before deprecating a flow.');
      return;
    }

    try {
      setActionInProgressId(identifierKey);
      await DeprecateFlow(flowIdentifier, currentBrand.brand_id);
      message.success('Flow deprecated successfully.');
      setFlows((prev) =>
        prev.map((item) => {
          const itemKey = toIdentifierKey(resolveFlowIdentifier(item));
          if (itemKey === identifierKey) {
            return { ...item, status: 'DEPRECATED' };
          }
          return item;
        })
      );
    } catch (error) {
      console.error('Failed to deprecate flow:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to deprecate flow. Please try again.';
      message.error(errorMessage);
    } finally {
      setActionInProgressId(null);
    }
  };

  const handleDeleteFlowAction = async (flow) => {
    if (!flow) return;
    if (flow.status !== 'DRAFT') {
      message.warning('Only draft flows can be deleted.');
      return;
    }

    const flowIdentifier = resolveFlowIdentifier(flow);
    const identifierKey = toIdentifierKey(flowIdentifier);

    if (!flowIdentifier || !identifierKey) {
      message.error('Unable to delete flow. Missing flow ID.');
      return;
    }

    if (!currentBrand?.brand_id) {
      message.error('Please select a brand before deleting a flow.');
      return;
    }

    try {
      setActionInProgressId(identifierKey);
      await deleteFlow(flowIdentifier, currentBrand.brand_id);
      message.success('Flow deleted successfully.');
      setFlows((prev) =>
        prev.filter((item) => {
          const itemKey = toIdentifierKey(resolveFlowIdentifier(item));
          return itemKey !== identifierKey;
        })
      );
      setPagination((prev) => ({
        ...prev,
        total: prev.total > 0 ? prev.total - 1 : 0,
      }));
    } catch (error) {
      console.error('Failed to delete flow:', error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete flow. Please try again.';
      message.error(errorMessage);
    } finally {
      setActionInProgressId(null);
    }
  };

  return (
    <div className="h-full">
      <div className="h-full max-w-7xl mx-auto">
        <div className="h-full bg-white rounded-lg shadow-sm mb-6">
          {isFlowsEnabled ? (
            <>
              <FlowListHeader
                inputRef={searchInputRef}
                loading={isLoading}
                searchKey={searchText}
                setSearchKey={setSearchText}
                onSearchChange={handleSearchChange}
                onFilterChange={handleFilterChange}
                selectedStatus={selectedStatus}
                clearAllFilter={handleClearFilters}
                handleSync={handleSync}
                isSyncing={isSyncing}
              />

              <FlowList
                flows={flows}
                isLoading={isLoading}
                isLoadingNextPage={isLoadingNextPage}
                hasMore={hasMore}
                loadNextPage={handleLoadMore}
                onFlowSelect={handleFlowSelect}
                onDeleteFlow={handleDeleteFlowAction}
                onDeprecateFlow={handleDeprecateFlowAction}
                actionInProgressId={actionInProgressId}
              />
            </>
          ) : (
            <PremiumUpgradeScreen/>
          )}
        </div>
      </div>
    </div>
  );
};

export default Flows;
